import { motion, AnimatePresence } from "framer-motion";
import { Moon, Star, Heart, Crown, Eye, Sun, RefreshCw, Flame, Globe, Droplets, Sparkles } from "lucide-react";
import { TAROT_CARDS } from "@/data";
import FreeReadingPromo from "@/components/FreeReadingPromo";

const ease = [0.16, 1, 0.3, 1];
const ICONS = { moon: Moon, star: Star, heart: Heart, crown: Crown, eye: Eye, sun: Sun, wheel: RefreshCw, flame: Flame, globe: Globe, droplets: Droplets };

// Deterministic "card of the day" so the draw stays consistent through the day.
export function cardOfTheDay() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return TAROT_CARDS[dayOfYear % TAROT_CARDS.length];
}

function Corner() {
  return <Star className="w-3 h-3 text-[#C9A24B]/70" fill="#C9A24B" strokeWidth={0} />;
}

export default function TarotCardFlip({ card, revealed, onReveal, onCta, testidPrefix = "tarot" }) {
  const Icon = ICONS[card.icon] || Sparkles;
  return (
    <div className="flex flex-col items-center">
      <div className="[perspective:1400px]">
        <motion.button
          onClick={() => !revealed && onReveal()}
          disabled={revealed}
          aria-label="Reveal your daily tarot card"
          data-testid={`${testidPrefix}-card`}
          whileHover={!revealed ? { y: -6 } : {}}
          className="relative w-[16rem] h-[24.5rem] [transform-style:preserve-3d]"
        >
          <motion.div
            className="absolute inset-0 [transform-style:preserve-3d]"
            animate={{ rotateY: revealed ? 180 : 0 }}
            transition={{ duration: 1, ease }}
          >
            {/* Face-down */}
            <div className="absolute inset-0 [backface-visibility:hidden] rounded-lg ornate-back flex flex-col items-center justify-center gap-6 cursor-pointer">
              <div className="absolute inset-2 rounded-md border border-[#C9A24B]/40" />
              <div className="absolute inset-[14px] rounded-md border border-[#C9A24B]/20" />
              <Moon className="w-14 h-14 text-[#C9A24B] glow-gold" strokeWidth={1} />
              <span className="text-[10px] tracking-[0.35em] uppercase text-[#C9A24B]/80">Tap to reveal</span>
              <div className="absolute top-4 left-4"><Corner /></div>
              <div className="absolute top-4 right-4"><Corner /></div>
              <div className="absolute bottom-4 left-4"><Corner /></div>
              <div className="absolute bottom-4 right-4"><Corner /></div>
            </div>

            {/* Revealed */}
            <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg ornate-face flex flex-col items-center justify-between py-7 px-6">
              <div className="absolute inset-2 rounded-md border border-[#C9A24B]/50 pointer-events-none" />
              <div className="absolute inset-[13px] rounded-md border border-[#C9A24B]/25 pointer-events-none" />
              <span className="font-display text-lg text-[#C9A24B] tracking-[0.2em]">{card.roman}</span>
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center justify-center w-20 h-20 rounded-full border border-[#C9A24B]/40 bg-[#140B09]/40 glow-gold">
                  <Icon className="w-10 h-10 text-[#E9D3A0]" strokeWidth={1} />
                </div>
                <h3 className="font-display text-3xl text-[#EFE3CC] leading-none text-center">{card.name}</h3>
              </div>
              <span className="font-display text-lg text-[#C9A24B] tracking-[0.2em] rotate-180">{card.roman}</span>
              <div className="absolute top-4 left-4"><Corner /></div>
              <div className="absolute top-4 right-4"><Corner /></div>
              <div className="absolute bottom-4 left-4"><Corner /></div>
              <div className="absolute bottom-4 right-4"><Corner /></div>
            </div>
          </motion.div>
        </motion.button>
      </div>

      <AnimatePresence>
        {revealed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease }}
            className="mt-9 flex flex-col items-center text-center"
            data-testid={`${testidPrefix}-reading`}
          >
            <p className="font-display italic text-xl sm:text-2xl leading-snug text-[#EFE3CC] max-w-lg">
              “{card.reading}”
            </p>
            <FreeReadingPromo onSecondary={onCta} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
